import React, {useState} from "react";
import {Button, Popconfirm, Row, Table, Tag, Tooltip, Input, Modal, message} from "antd";
import {HistoryOutlined, InfoCircleOutlined} from "@ant-design/icons";
import {useStore} from "../../../../../hooks/useStore";
import moment from "moment";
import {DocumentRejectHistoryModal} from "../documentRejectHistoryModal/DocumentRejectHistoryModal";

const MIN_REJECT_REASON_LENGTH = 15;
export const CourierDocuments = ({courier, reloadCourier, loading}) => {
    const {documents} = courier;
    const stores = useStore();
    const documentNameMap = {
        drivingLicenseFront: "Ehliyet Ön Yüz",
        drivingLicenseBack: "Ehliyet Arka Yüz",
        criminalRecord: "Adli sicil kaydı",
        personCompanyTaxCertificate: "Şahıs şirketi vergi levhası",
        penaltyPointsDocument: "Sürücü Belgesi ve Şahıslara Yazılan Ceza Puanı Belgesi",
        residenceCertificate: "İkametgah belgesi",
        identityCardFront: "Kimlik ön yüz",
        identityCardBack: "Kimlik arka yüz",
        vehicleLicense: "Araç ruhsatı",
        srcCertificate: "SRC belgesi",
        psychotechnicalCertificate: "Psikoteknik belgesi",
        profilePicture: "Profil Resmi",
        p1Certificate: "P1* Belgesi"
    };

    const [documentRejectHistoryModal, setDocumentRejectHistoryModal] = useState(false);
    const [
        documentRejectHistoryModalDocumentType,
        setDocumentRejectHistoryModalDocumentType
    ] = useState(null);
    const [documentViewerModal, setDocumentViewerModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const getDocumentName = key => documentNameMap[key] || key;

    const [documentRejectReason, setDocumentRejectReason] = useState("");

    const documentStatusColorMap = {
        waitingApprove: "orange",
        approved: "green",
        rejected: "red"
    };
    const getDocumentStatusColor = status => documentStatusColorMap[status] || "#919190";

    const documentStatusTextMap = {
        waitingApprove: "Onay Bekliyor",
        approved: "Onaylandı",
        rejected: "Reddedildi"
    };
    const getDocumentStatusText = status =>
        documentStatusTextMap[status] || status || "Belge Yüklenmemiş";

    const approvalTooltipLabel = (documentName, documentStatus, actionLabel = "") => {
        if (!documentName) {
            return "Belge Yüklenmemiş";
        }
        if (documentStatus === "approved") {
            return "Belge Zaten Onaylanmış";
        } else if (documentStatus === "rejected") {
            return "Belge Zaten Reddedilmiş";
        } else if (courier?.isApplicationRejected && courier?.onboardingStatus !== "blocked") {
            return "Kurye adayının başvurusu olumsuz işaretlendiği için belge onayı yapılamaz.";
        } else if (courier?.onboardingStatus === "blocked") {
            return "Kurye, kara listede olduğu için belge onayı yapılamaz.";
        } else if (!checkPermission("approveDocuments")) {
            return "Kurye adayı, belge kontrol işlemi talep etmemiş.";
        } else {
            return actionLabel;
        }
    };

    const approveDocument = async key => {
        const result = await stores.courierStore.updateCourierDocumentStatus({
            courierId: courier._id,
            documentType: key,
            status: "approved"
        });
        if (result) {
            reloadCourier();
            closeDocumentViewerModal();
        }
    };
    const rejectDocument = async (key, documentRejectReason) => {
        if (!documentRejectReason) {
            message.error("Lütfen reddetme sebebini giriniz.");
            return;
        }
        if (documentRejectReason?.trim()?.length < MIN_REJECT_REASON_LENGTH) {
            message.error(
                `Lütfen reddetme sebebini en az ${MIN_REJECT_REASON_LENGTH} karakter olacak şekilde giriniz.`
            );
            return;
        }
        try {
            const result = await stores.courierStore.updateCourierDocumentStatus({
                courierId: courier._id,
                documentType: key,
                status: "rejected",
                reason: documentRejectReason
            });
            if (result) {
                reloadCourier();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setDocumentRejectReason("");
            closeDocumentViewerModal();
        }
    };

    const checkPermission = ({key, documentType}) => {
        const onboardingStatus = courier?.onboardingStatus;
        const onboardingSubStatus = courier?.onboardingSubStatus;
        const onboardingStep = courier?.onboardingStep;
        const isApplicationRejected = Boolean(courier?.isApplicationRejected);

        const permissions = {
            approveDocuments:
                !stores.meStore.checkRole("isCompanyUser") &&
                ((onboardingStatus === "candidateCandidate" &&
                    onboardingSubStatus === "documentsInReview") ||
                    ["p1Certificate"].includes(documentType)) &&
                !isApplicationRejected
        };

        return permissions?.[key] || false;
    };

    const openDocumentRejectHistoryModal = documentType => {
        setDocumentRejectHistoryModalDocumentType(documentType);
        setDocumentRejectHistoryModal(true);
    };
    const closeDocumentRejectHistoryModal = () => {
        setDocumentRejectHistoryModalDocumentType(null);
        setDocumentRejectHistoryModal(false);
    };

    const openDocumentViewerModal = document => {
        setSelectedDocument(document);
        setDocumentViewerModal(true);
    };

    const closeDocumentViewerModal = () => {
        setSelectedDocument(null);
        setDocumentViewerModal(false);
    };

    const renderActionButtons = document => {
        return (
            <Row justify="center">
                {["approved", "rejected"].includes(document?.status) ? (
                    <Tag key={document.status} color={getDocumentStatusColor(document.status)}>
                        {getDocumentStatusText(document.status)}
                    </Tag>
                ) : (
                    <>
                        <Tooltip
                            title={approvalTooltipLabel(
                                document?.name,
                                document?.status,
                                `Belgeyi Onayla`
                            )}
                            placement="bottom"
                        >
                            <Popconfirm
                                placement="topRight"
                                icon={<></>}
                                title={"Belgeyi onaylamak istediğinize emin misiniz?"}
                                overlayStyle={{
                                    width: "300px"
                                }}
                                onConfirm={() => approveDocument(document.key)}
                                okText={"Onayla"}
                                cancelText={"İptal"}
                                disabled={
                                    !document?.name ||
                                    ["approved", "rejected"].includes(document?.status) ||
                                    !checkPermission({
                                        key: "approveDocuments",
                                        documentType: document.key
                                    })
                                }
                            >
                                <Button
                                    loading={loading}
                                    type="primary"
                                    size="small"
                                    style={{
                                        backgroundColor: "green",
                                        borderColor: "green",
                                        color: "white",
                                        opacity:
                                            document?.name &&
                                            !["approved", "rejected"].includes(document?.status) &&
                                            checkPermission({
                                                key: "approveDocuments",
                                                documentType: document.key
                                            })
                                                ? 1
                                                : 0.5
                                    }}
                                    disabled={
                                        !document?.name ||
                                        ["approved", "rejected"].includes(document?.status) ||
                                        !checkPermission({
                                            key: "approveDocuments",
                                            documentType: document.key
                                        })
                                    }
                                >
                                    Onayla
                                </Button>
                            </Popconfirm>
                        </Tooltip>
                        <Tooltip
                            title={approvalTooltipLabel(
                                document?.name,
                                document?.status,
                                `Belgeyi Reddet`
                            )}
                            placement="bottom"
                        >
                            <Popconfirm
                                placement="topRight"
                                icon={<></>}
                                title={
                                    <>
                                        <p>Belgeyi reddetmek istediğinize emin misiniz? </p>
                                        <p>
                                            Kurye adayına geri bildirim vermek için lütfen belgeyi
                                            neden reddettiğinizi belirtin.
                                        </p>
                                        <Input.TextArea
                                            placeholder="Kuryeye sms ve mail olarak gönderilecek olan red nedenini giriniz."
                                            style={{marginTop: "10px"}}
                                            value={documentRejectReason}
                                            onChange={e => {
                                                setDocumentRejectReason(e.target.value);
                                            }}
                                            rows={4}
                                        />
                                    </>
                                }
                                overlayStyle={{
                                    width: "300px"
                                }}
                                onVisibleChange={() => setDocumentRejectReason("")}
                                onConfirm={() => rejectDocument(document.key, documentRejectReason)}
                                okText={"Reddet"}
                                cancelText={"İptal"}
                                okButtonProps={{disabled: !documentRejectReason}}
                                disabled={
                                    !document?.name ||
                                    ["approved", "rejected"].includes(document?.status) ||
                                    !checkPermission({
                                        key: "approveDocuments",
                                        documentType: document.key
                                    })
                                }
                            >
                                <Button
                                    loading={loading}
                                    type="primary"
                                    size="small"
                                    style={{
                                        marginLeft: "5px",
                                        backgroundColor: "red",
                                        borderColor: "red",
                                        color: "white",
                                        opacity:
                                            document?.name &&
                                            !["approved", "rejected"].includes(document?.status) &&
                                            checkPermission({
                                                key: "approveDocuments",
                                                documentType: document.key
                                            })
                                                ? 1
                                                : 0.5
                                    }}
                                    onClick={() => setDocumentRejectReason("")}
                                    disabled={
                                        !document?.name ||
                                        ["approved", "rejected"].includes(document?.status) ||
                                        !checkPermission({
                                            key: "approveDocuments",
                                            documentType: document?.key
                                        })
                                    }
                                >
                                    Reddet
                                </Button>
                            </Popconfirm>
                        </Tooltip>
                    </>
                )}
                <Tooltip title="Reddedilen Versiyonlar">
                    <Button
                        size="small"
                        icon={<HistoryOutlined />}
                        style={{
                            marginLeft: "5px"
                        }}
                        onClick={() => openDocumentRejectHistoryModal(document?.key)}
                    />
                </Tooltip>
            </Row>
        );
    };

    return (
        <>
            <Table
                loading={loading}
                pagination={false}
                columns={[
                    {
                        title: "Belge Türü",
                        dataIndex: "key",
                        key: "key",
                        render: (key, document) => (
                            <Row align="middle">
                                {getDocumentName(key)}

                                {document?.name ? (
                                    <Tooltip title={document?.name}>
                                        <InfoCircleOutlined style={{marginLeft: "5px"}} />
                                    </Tooltip>
                                ) : null}
                            </Row>
                        ),
                        sorter: (a, b) =>
                            getDocumentName(a.key).localeCompare(getDocumentName(b.key)),
                        defaultSortOrder: "ascend"
                    },
                    {
                        title: "Durum",
                        dataIndex: "status",
                        key: "status",
                        render: status => (
                            <Tag color={getDocumentStatusColor(status)} key={status}>
                                {getDocumentStatusText(status)}
                            </Tag>
                        )
                    },
                    {
                        title: "Link",
                        dataIndex: "publicUrl",
                        key: "publicUrl",
                        render: (url, document) =>
                            url ? (
                                <Button
                                    type="primary"
                                    onClick={() => openDocumentViewerModal(document)}
                                    size="small"
                                >
                                    Görüntüle
                                </Button>
                            ) : (
                                "-"
                            )
                    },

                    {
                        title: "Onayla / Reddet",
                        align: "center",
                        render: (_, document) => renderActionButtons(document)
                    }
                ]}
                dataSource={Object.entries(documents)
                    .map(([key, value]) => ({
                        key: key,
                        name: value?.name,
                        publicUrl: value?.file?.[0]?.publicUrl,
                        status: value?.status
                    }))
                    .sort((a, b) => getDocumentName(a.key).localeCompare(getDocumentName(b.key)))}
            />
            <DocumentRejectHistoryModal
                title={`Reddedilen Versiyonlar (${getDocumentName(
                    documentRejectHistoryModalDocumentType
                )})`}
                visible={documentRejectHistoryModal}
                onClose={closeDocumentRejectHistoryModal}
                courierId={courier._id}
                documentType={documentRejectHistoryModalDocumentType}
            />
            <Modal
                title={
                    selectedDocument ? getDocumentName(selectedDocument.key) : "Belge Görüntüleyici"
                }
                visible={documentViewerModal}
                onCancel={closeDocumentViewerModal}
                footer={
                    selectedDocument ? (
                        <Row justify="space-between" align="middle">
                            <Button
                                type="default"
                                size="small"
                                onClick={() => window.open(selectedDocument.publicUrl, "_blank")}
                            >
                                İndir
                            </Button>
                            {renderActionButtons(selectedDocument)}
                        </Row>
                    ) : null
                }
                width={800}
                style={{top: 50}}
                bodyStyle={{
                    padding: 0,
                    height: "600px"
                }}
            >
                <iframe
                    src={selectedDocument?.publicUrl}
                    style={{
                        width: "100%",
                        height: "100%",
                        border: "none"
                    }}
                />
            </Modal>
        </>
    );
};
